"use client";
import { userProfileKey } from "~/src/constants/user-profile-key";

import { useMemo } from "react";
import { create } from "zustand";
import {
  type DeviceType,
  type UserInfoType,
} from "../models/profile/user-info";
import { UserStatusType, type RoomAvatarType } from "../models/secure-chat";
import { useConnection } from "wagmi";
import { addressShorten } from "../utils/wallet";
import { getUserBasicInfo } from "../utils/user-info";
import { useGetUserDisplayName } from "./use-user-profile-store";
import { useQueryClient } from "@tanstack/react-query";

export interface AuthState {
  isLoggedIn: boolean;
  userInfo: UserInfoType | null;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setUserInfo: (userInfo: UserInfoType | null) => void;
  resetAuthState: () => void;
  isSigningMessage: boolean;
  setIsSigningMessage: (isSigningMessage: boolean) => void;
}

const initialState = {
  isLoggedIn: false,
  userInfo: null,
  isSigningMessage: false,
};

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState,

  setUserInfo: (userInfo) => {
    set({ userInfo });
  },

  setIsLoggedIn: (isLoggedIn) => {
    set({ isLoggedIn });
  },

  setIsSigningMessage: (isSigningMessage) => {
    set({ isSigningMessage });
  },

  resetAuthState: () => {
    set(initialState);
  },
}));

export const useUserBasicInfo = (): {
  userStatus: UserStatusType | undefined;
  userDevices: Set<string>;
} => {
  const userInfo = useAuthStore((state) => state.userInfo);
  const userStatus = userInfo?.status;
  const userDevices = useMemo(
    () =>
      new Set<string>(
        userInfo?.devices?.map((item: DeviceType) => item.device_token) ?? []
      ),
    [userInfo?.devices]
  );

  return { userStatus, userDevices };
};

export const useCurrentUserId = (): string => {
  const userId = useAuthStore((state) => state.userInfo?.id) ?? "";
  return userId;
};

export const useUserDisplayName = (): string => {
  const { address } = useConnection();
  const displayName =
    useAuthStore((state) => state.userInfo?.display_name) ??
    addressShorten(address, 4);
  return displayName;
};

export const useUserAvatar = (): {
  userAvatar: RoomAvatarType[];
  avatarUrl: string | null;
  nftAvatarUrl: string | null;
  hasAvatar: boolean;
} => {
  const userInfo = useAuthStore((state) => state.userInfo);
  const { getDisplayName } = useGetUserDisplayName();
  const { avatarUrl, nftAvatarUrl, avatars } = getUserBasicInfo(
    userInfo,
    getDisplayName
  );
  const hasAvatar = !!(avatarUrl ?? nftAvatarUrl);

  const userAvatar = avatars;
  return { userAvatar, avatarUrl, nftAvatarUrl, hasAvatar };
};

export const useIsInActiveUser = (): boolean => {
  const userStatus = useAuthStore((state) => state.userInfo?.status);
  const isInActive = userStatus === UserStatusType.INACTIVE;
  return isInActive;
};

export const useUpdateUserProfile = (): {
  onUpdateUserProfile: (userInfo: UserInfoType | null) => void;
} => {
  const queryClient = useQueryClient();
  const { address } = useConnection();

  const onUpdateUserProfile = (userInfo: UserInfoType | null): void => {
    if (!userInfo) return;
    queryClient.setQueryData(userProfileKey(address), userInfo);
  };
  return { onUpdateUserProfile };
};
